import { useTranslation } from "react-i18next";

import { DetailRow, DetailRows } from "@/components/shared/DetailRows";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function num(v: unknown): number | null {
  return typeof v === "number" && (v === 6 || v === 12 || v === 24) ? v : null;
}

function bool(v: unknown): boolean {
  return v === true;
}

export function IncomeCertificateSummaryCard({
  details,
}: {
  details: Record<string, unknown> | null | undefined;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (!details || typeof details !== "object") return null;

  const periodMonths = num(details.periodMonths);
  const presentingEntity = str(details.presentingEntity);
  const presentingInstitution = str(details.presentingInstitution);
  const activity = str(details.activity);
  const activityCustom = str(details.activityCustom);
  const workModality = str(details.workModality);
  const bpsNumber = str(details.bpsNumber);
  const specialFormat = bool(details.specialFormatRequested);

  const activityLabel =
    activity === "other" && activityCustom
      ? activityCustom
      : activity
        ? t(`requests.user.incomeWizard.activity.${activity}`, { defaultValue: activity })
        : "—";

  const modalityLabel = workModality
    ? t(`requests.user.incomeWizard.modalitySummary.${workModality}`, { defaultValue: workModality })
    : "—";

  const periodLabel =
    periodMonths != null
      ? t(`requests.user.incomeWizard.period${periodMonths}`)
      : "—";

  const rows: Array<{ label: string; value: string }> = [
    { label: t("requests.user.incomeCertificateSummary.period"), value: periodLabel },
    { label: t("requests.user.incomeCertificateSummary.presentingEntity"), value: presentingEntity || "—" },
    // Omitted rather than dashed when absent: most categories never ask for an
    // institution, so an unconditional row would show a permanent "—".
    ...(presentingInstitution
      ? [{
          label: t("requests.user.incomeCertificateSummary.presentingInstitution"),
          value: presentingInstitution,
        }]
      : []),
    { label: t("requests.user.incomeCertificateSummary.activity"), value: activityLabel },
    { label: t("requests.user.incomeCertificateSummary.modality"), value: modalityLabel },
    {
      label: t("requests.user.incomeCertificateSummary.specialFormat"),
      value: specialFormat ? t("requests.user.incomeWizard.specialFormatYes") : t("requests.user.incomeWizard.specialFormatNo"),
    },
  ];
  if (bpsNumber) {
    rows.push({ label: t("requests.user.incomeCertificateSummary.bpsNumber"), value: bpsNumber });
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-foreground">
          {t("requests.user.incomeCertificateSummary.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <DetailRows>
          {rows.map((row) => (
            <DetailRow key={row.label} label={row.label}>
              {row.value}
            </DetailRow>
          ))}
        </DetailRows>
      </CardContent>
    </Card>
  );
}
